import { cachedJSON } from '../utils/cachedJSON.js';
import { useState, useEffect, useRef } from 'react';
import { SEVERITY, CATEGORY } from './useEventBus.js';
import { pushToast } from '../components/AlertToast.jsx';

const API = import.meta.env.VITE_API_BASE || '/api';

// ── Active Launches ───────────────────────────────────────────────────────────
export function useLaunches(enabled, push) {
  const [launches, setLaunches] = useState([]);
  const alerted = useRef(new Set());

  useEffect(() => {
    if (!enabled) { setLaunches([]); return; }
    let cancelled = false;

    async function fetch_() {
      try {
        const data = await cachedJSON(`${API}/launches`);
        const list = data.launches || [];
        if (!cancelled) {
          setLaunches(list);
          for (const l of list) {
            if (!l.net || l.status !== 'Go') continue;
            const hoursToNet = (new Date(l.net) - Date.now()) / 3600000;
            if (hoursToNet < 0 || hoursToNet > 2) continue; // only within 2h
            const key = `${l.id}-${hoursToNet < 0.5 ? 'crit' : 'warn'}`;
            if (!alerted.current.has(key)) {
              alerted.current.add(key);
              const sev = hoursToNet < 0.5 ? SEVERITY.WARNING : SEVERITY.NOTICE;
              push?.({ severity: sev, category: CATEGORY.SYSTEM,
                title: `🚀 Launch in ${Math.round(hoursToNet * 60)}m`,
                body:  `${l.name} · ${l.pad_location || l.pad_name || ''}`,
                lat: l.lat, lon: l.lon });
              if (hoursToNet < 0.5) pushToast({ severity: sev,
                title: `🚀 Launch in ${Math.round(hoursToNet * 60)}m`,
                body:  l.name, lat: l.lat, lon: l.lon });
            }
          }
        }
      } catch { /* keep last */ }
    }

    fetch_();
    const id = setInterval(fetch_, 60 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled]);

  return { launches };
}

// Reentry paths are illustrative propagation, not landing predictions.
// Only fetch a short segment for the selected object; the overview uses markers.
export function tierFor(decay_epoch, uncertainty_days) {
  const hours = (new Date(decay_epoch) - Date.now()) / 3600000;
  if (!Number.isFinite(hours) || (uncertainty_days || 0) > 1 || hours > 72) return 'far';
  if (hours > 24) return 'distant';
  if (hours > 6) return 'soon';
  return 'imminent';
}

export const TIER_CONFIG = {
  imminent: { color: '#ff5050', alpha: 0.85 },
  soon:     { color: '#ff7460', alpha: 0.72 },
  distant:  { color: '#ffaa44', alpha: 0.60 },
};

const _corridorCache = new Map();
const CACHE_TTL = 10 * 60 * 1000;

export function useReentryCorridors(reentries, selected, enabled) {
  const [corridors, setCorridors] = useState({});
  const selectedId = selected?.reentry?.norad_id ?? selected?.norad_id;
  const reentry = reentries?.find(r => String(r.norad_id) === String(selectedId));
  const tier = reentry ? tierFor(reentry.decay_epoch, reentry.uncertainty_days) : null;

  useEffect(() => {
    setCorridors({});
    if (!enabled || !reentry || tier === 'far') return;
    const id = reentry.norad_id;
    const cached = _corridorCache.get(id);
    if (cached && cached.tier === tier && Date.now() - cached.ts < CACHE_TTL) {
      setCorridors({ [id]: { tier, points: cached.points } });
      return;
    }
    const controller = new AbortController();
    fetch(`${API}/satellites/${id}/track?minutes_back=0&minutes_ahead=90`, { signal: controller.signal })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (controller.signal.aborted || !data?.track?.length) return;
        _corridorCache.set(id, { tier, points: data.track, ts: Date.now() });
        if (_corridorCache.size > 30) _corridorCache.delete(_corridorCache.keys().next().value);
        setCorridors({ [id]: { tier, points: data.track } });
      })
      .catch(error => { if (error.name !== 'AbortError') console.error('[corridor]', id, error); });
    return () => controller.abort();
  }, [enabled, reentry?.norad_id, tier]);

  return { corridors };
}
